import { PlanningExpertise } from '../types/planning.types';

/**
 * Get expertises for a specific date
 */
export const getExpertisesForDate = (
  date: Date,
  expertises: PlanningExpertise[]
): PlanningExpertise[] => {
  const expertisesForDate = expertises.filter(expertise => {
    if (!expertise.date_du_rv) return false;
    const rvDate = new Date(expertise.date_du_rv);
    return (
      rvDate.getFullYear() === date.getFullYear() &&
      rvDate.getMonth() === date.getMonth() &&
      rvDate.getDate() === date.getDate()
    );
  });
  
  // Sort by time (heure_rv)
  return expertisesForDate.sort((a, b) => {
    if (!a.heure_rv && !b.heure_rv) return 0;
    if (!a.heure_rv) return 1;
    if (!b.heure_rv) return -1;
    return a.heure_rv.localeCompare(b.heure_rv);
  });
};

/**
 * Find the first week with at least one appointment
 */
export const findFirstWeekWithAppointment = (
  expertises: PlanningExpertise[]
): number => {
  if (!expertises || expertises.length === 0) return 0;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  // Calculate current week's Monday
  const currentDay = today.getDay();
  const diff = currentDay === 0 ? -6 : 1 - currentDay;
  const currentWeekMonday = new Date(today);
  currentWeekMonday.setDate(today.getDate() + diff);
  currentWeekMonday.setHours(0, 0, 0, 0);
  
  // Get all expertise dates that have appointments from today onwards
  const futureAppointments = expertises
    .filter(exp => {
      if (!exp.date_du_rv) return false;
      const appointmentDate = new Date(exp.date_du_rv);
      appointmentDate.setHours(0, 0, 0, 0);
      
      // Calculate the Monday of the appointment's week
      const appointmentDay = appointmentDate.getDay();
      const appointmentDiff = appointmentDay === 0 ? -6 : 1 - appointmentDay;
      const appointmentWeekMonday = new Date(appointmentDate);
      appointmentWeekMonday.setDate(appointmentDate.getDate() + appointmentDiff);
      appointmentWeekMonday.setHours(0, 0, 0, 0);
      
      // Only include if the appointment's week is current week or future
      return appointmentWeekMonday >= currentWeekMonday;
    })
    .map(exp => new Date(exp.date_du_rv!))
    .sort((a, b) => a.getTime() - b.getTime());
  
  if (futureAppointments.length === 0) return 0; // Default to current week if no future appointments
  
  // Find the earliest future appointment date
  const earliestDate = futureAppointments[0];
  
  // Calculate the Monday of the week containing the earliest appointment
  const earliestDay = earliestDate.getDay();
  const earliestDiff = earliestDay === 0 ? -6 : 1 - earliestDay;
  const earliestWeekMonday = new Date(earliestDate);
  earliestWeekMonday.setDate(earliestDate.getDate() + earliestDiff);
  earliestWeekMonday.setHours(0, 0, 0, 0);
  
  // Calculate the week offset (will be 0 or positive)
  const weekDiff = Math.round((earliestWeekMonday.getTime() - currentWeekMonday.getTime()) / (1000 * 60 * 60 * 24 * 7));
  
  return Math.max(0, weekDiff); // Ensure we never return negative (past) weeks
};

/**
 * Format address for display
 */
export const formatAddress = (address: string[] | undefined): string => {
  if (!address || address.length === 0) return 'Adresse non définie';
  
  const street = address[0] || '';
  const city = address[1] || '';
  
  if (street && city) return `${street}, ${city}`;
  return street || city || 'Adresse non définie';
};